import { api, useApi } from "../../configuration/api/apiHelper";
import { UpdateUserCategoryRatesCommand, UserCategoryViewModel, GeneratePersonaInquiryResult, CategoryViewModel, UpdateUserCategoriesCommand, AddUserCategoryViewModel } from "../../configuration/api/apiSchema";

// Select user categories
export enum SELECT_USER_CATEGORIES {
    REQUEST = "SELECT_USER_CATEGORIES_REQUEST",
    SUCCESS = "SELECT_USER_CATEGORIES_SUCCESS",
}

export const selectUserCategoriesRequestAction = () => {
    return {
        type: SELECT_USER_CATEGORIES.REQUEST
    };
};

export const selectUserCategoriesSuccessAction = () => {
    return {
        type: SELECT_USER_CATEGORIES.SUCCESS,
    };
};

export const selectUserCategories = (categoryKeys: string[]) => {
    return async (dispatch: any) => {
        dispatch(selectUserCategoriesRequestAction());

        const response = await useApi(() =>
            api.userOnboarding.selectCategoriesCreate({
                categoryKeys: categoryKeys
            }));

        if (response.result) {
            dispatch(selectUserCategoriesSuccessAction());
            // Import and dispatch refreshToken to refresh user status in JWT
            const { refreshToken } = await import('../Auth/actions');
            const authData = JSON.parse(localStorage.getItem('authData') || '{}');
            if (authData.refresh_token) {
                dispatch(refreshToken(authData.refresh_token));
            }
            // Fetch user selected categories to populate profile
            dispatch(getUserCategories());
        }
        else {
            // TODO: error
            // dispatch(throwError(response.toString()));
        }
    };
};

// Get user categories
export enum GET_USER_CATEGORIES {
    REQUEST = "GET_USER_CATEGORIES_REQUEST",
    SUCCESS = "GET_USER_CATEGORIES_SUCCESS",
}

export const getUserCategoriesRequestAction = () => {
    return {
        type: GET_USER_CATEGORIES.REQUEST
    };
};

export const getUserCategoriesSuccessAction = (userCategories: UserCategoryViewModel[]) => {
    return {
        type: GET_USER_CATEGORIES.SUCCESS,
        userCategories: userCategories
    };
};

export const getUserCategories = (userId?: string) => {
    return async (dispatch: any) => {
        dispatch(getUserCategoriesRequestAction());

        const response = await useApi(() =>
            api.categories.userSelectedList({
                userId: userId
            }));

        if (response.result) {
            dispatch(getUserCategoriesSuccessAction(response.result));
        }
        else {
            // TODO: error
            // dispatch(throwError(response.toString()));
        }
    };
};

// NEW: Get all available categories
export enum GET_ALL_CATEGORIES {
    REQUEST = "GET_ALL_CATEGORIES_REQUEST",
    SUCCESS = "GET_ALL_CATEGORIES_SUCCESS",
    FAILURE = "GET_ALL_CATEGORIES_FAILURE",
}

export const getAllCategoriesRequestAction = () => {
    return {
        type: GET_ALL_CATEGORIES.REQUEST
    };
};

export const getAllCategoriesSuccessAction = (categories: CategoryViewModel[]) => {
    return {
        type: GET_ALL_CATEGORIES.SUCCESS,
        categories: categories
    };
};

export const getAllCategoriesFailureAction = (error: string) => {
    return {
        type: GET_ALL_CATEGORIES.FAILURE,
        error: error
    };
};

export const getAllCategories = (languageKey?: string) => {
    return async (dispatch: any) => {
        dispatch(getAllCategoriesRequestAction());

        const response = await useApi(() =>
            api.categories.categoriesList({
                languageKey: languageKey
            }));

        if (response.result) {
            dispatch(getAllCategoriesSuccessAction(response.result));
        }
        else {
            dispatch(getAllCategoriesFailureAction(response.error || 'Failed to fetch categories'));
        }
    };
};

// NEW: Add user categories
export enum ADD_USER_CATEGORIES {
    REQUEST = "ADD_USER_CATEGORIES_REQUEST",
    SUCCESS = "ADD_USER_CATEGORIES_SUCCESS",
    FAILURE = "ADD_USER_CATEGORIES_FAILURE",
}

export const addUserCategoriesRequestAction = () => {
    return {
        type: ADD_USER_CATEGORIES.REQUEST
    };
};

export const addUserCategoriesSuccessAction = () => {
    return {
        type: ADD_USER_CATEGORIES.SUCCESS,
    };
};

export const addUserCategoriesFailureAction = (error: string) => {
    return {
        type: ADD_USER_CATEGORIES.FAILURE,
        error: error
    };
};

// Helper function to convert CategoryViewModel to AddUserCategoryViewModel
const convertCategoryToAddUserCategory = (category: CategoryViewModel): AddUserCategoryViewModel => ({
    name: category.name,
    key: category.key,
    isRootCategory: category.isRootCategory,
    parentCategoryKey: category.parentCategoryKey,
    hourlyRate: undefined,
    hourlyRateCurrencyKey: undefined,
    fixedPriceRate: undefined,
    fixedPriceRateCurrencyKey: undefined,
});

// Helper function to convert UserCategoryViewModel to AddUserCategoryViewModel
const convertUserCategoryToAddUserCategory = (userCategory: UserCategoryViewModel): AddUserCategoryViewModel => ({
    name: userCategory.name,
    key: userCategory.key,
    isRootCategory: userCategory.isRootCategory,
    parentCategoryKey: userCategory.parentCategoryKey,
    hourlyRate: userCategory.hourlyRate,
    hourlyRateCurrencyKey: userCategory.hourlyRateCurrencyKey,
    fixedPriceRate: userCategory.fixedPriceRate,
    fixedPriceRateCurrencyKey: userCategory.fixedPriceRateCurrencyKey,
});

export const addUserCategories = (newCategories: CategoryViewModel[], existingCategories: UserCategoryViewModel[]) => {
    return async (dispatch: any) => {
        dispatch(addUserCategoriesRequestAction());

        // Convert new categories to AddUserCategoryViewModel objects
        const newCategoriesFormatted: AddUserCategoryViewModel[] = newCategories.map(convertCategoryToAddUserCategory);
        
        // Convert existing categories to AddUserCategoryViewModel objects
        const existingCategoriesFormatted: AddUserCategoryViewModel[] = existingCategories.map(convertUserCategoryToAddUserCategory);

        // Combine existing and new categories
        const allCategories = [...existingCategoriesFormatted, ...newCategoriesFormatted];

        const command: UpdateUserCategoriesCommand = {
            categories: allCategories
        };

        const response = await useApi(() =>
            api.categories.userUpdate(command));

        if (response.result !== undefined) {
            dispatch(addUserCategoriesSuccessAction());
            // Refresh user categories after successful addition
            dispatch(getUserCategories());
        }
        else {
            dispatch(addUserCategoriesFailureAction(response.error || 'Failed to add categories'));
        }
    };
};

// NEW: Remove user categories
export enum REMOVE_USER_CATEGORIES {
    REQUEST = "REMOVE_USER_CATEGORIES_REQUEST",
    SUCCESS = "REMOVE_USER_CATEGORIES_SUCCESS",
    FAILURE = "REMOVE_USER_CATEGORIES_FAILURE",
}

export const removeUserCategoriesRequestAction = () => {
    return {
        type: REMOVE_USER_CATEGORIES.REQUEST
    };
};

export const removeUserCategoriesSuccessAction = () => {
    return {
        type: REMOVE_USER_CATEGORIES.SUCCESS,
    };
};

export const removeUserCategoriesFailureAction = (error: string) => {
    return {
        type: REMOVE_USER_CATEGORIES.FAILURE,
        error: error
    };
};

export const removeUserCategories = (categoryKeysToRemove: string[], existingCategories: UserCategoryViewModel[]) => {
    return async (dispatch: any) => {
        dispatch(removeUserCategoriesRequestAction());

        // Filter out the categories to be removed
        const remainingCategories = existingCategories.filter(cat => !categoryKeysToRemove.includes(cat.key || ''));
        
        // Convert remaining categories to AddUserCategoryViewModel objects
        const categories: AddUserCategoryViewModel[] = remainingCategories.map(convertUserCategoryToAddUserCategory);

        const command: UpdateUserCategoriesCommand = {
            categories: categories
        };

        const response = await useApi(() =>
            api.categories.userUpdate(command));

        if (response.result !== undefined) {
            dispatch(removeUserCategoriesSuccessAction());
            // Refresh user categories after successful removal
            dispatch(getUserCategories());
        }
        else {
            dispatch(removeUserCategoriesFailureAction(response.error || 'Failed to remove categories'));
        }
    };
};

export const openStripeOnboardingLink = () => {
    return async (dispatch: any) => {
      try {
        const rawResponse = await api.users.stripeOnboardingLinkList() as Response;
  
        // ✅ Parse the JSON body
        const data = await rawResponse.json();
  
        console.log("Parsed onboarding link response:", data);
  
        const link = data?.url;
  
        if (link && link.startsWith("https://connect.stripe.com")) {
          window.open(link, "_blank", "noopener,noreferrer");
        } else {
          console.error("Invalid onboarding URL:", data);
        }
      } catch (error) {
        console.error("Failed to open Stripe onboarding link:", error);
      }
    };
  };

// Update user category rates
export enum UPDATE_USER_CATEGORY_RATE {
    REQUEST = "UPDATE_USER_CATEGORY_RATE_REQUEST",
    SUCCESS = "UPDATE_USER_CATEGORY_RATE_SUCCESS",
}

export const updateUserCategoryRateRequestAction = () => {
    return {
        type: UPDATE_USER_CATEGORY_RATE.REQUEST
    };
};

export const updateUserCategoryRateSuccessAction = (updatedUserCategory: UserCategoryViewModel) => {
    return {
        type: UPDATE_USER_CATEGORY_RATE.SUCCESS,
        updatedUserCategory: updatedUserCategory
    };
};

export const updateUserCategoryRate = (userCategory: UserCategoryViewModel) => {
    return async (dispatch: any) => {
        dispatch(updateUserCategoryRateRequestAction());

        const command = {
            categoryKey: userCategory.key,
            fixedPriceRate: userCategory.fixedPriceRate,
            fixedPriceRateCurrencyKey: userCategory.fixedPriceRateCurrencyKey,
            hourlyRate: userCategory.hourlyRate,
            hourlyRateCurrencyKey: userCategory.hourlyRateCurrencyKey
        } as UpdateUserCategoryRatesCommand;

        const response = await useApi(() =>
            api.categories.categoriesUpdate(command));

        if (!response.error) {
            dispatch(updateUserCategoryRateSuccessAction(userCategory));
        }
        else {
            // TODO: error
            // dispatch(throwError(response.toString()));
        }
    };
};

export enum START_USER_KYC {
  REQUEST = "START_USER_KYC_REQUEST",
  SUCCESS = "START_USER_KYC_SUCCESS",
  FAILURE = "START_USER_KYC_FAILURE",
}

const startUserKycRequestAction = () => ({ type: START_USER_KYC.REQUEST });
const startUserKycSuccessAction = (payload: GeneratePersonaInquiryResult) => ({
  type: START_USER_KYC.SUCCESS,
  payload,
});
const startUserKycFailureAction = (message: string) => ({
  type: START_USER_KYC.FAILURE,
  message,
});

// ── 2. thunk ──────────────────────────────────────────────────
export const startUserKyc = () => {
  return async (dispatch: any) => {
    dispatch(startUserKycRequestAction());

    try {
      const raw: Response = await api.user.verificationPersonaStartCreate({
        format: undefined as any,   // disable auto-json
      });

      // ---- 404 – do nothing ----------------------------------
      if (raw.status === 404) {
        dispatch(startUserKycFailureAction("endpoint not found"));
        return null;
      }

      // ---- other non-success ---------------------------------
      if (!raw.ok) {
        const errText = await raw.text();          // may be empty
        dispatch(startUserKycFailureAction(errText || raw.statusText));
        return null;                               // ⬅️  don't throw
      }

      // ---- happy path ----------------------------------------
      const data: GeneratePersonaInquiryResult = await raw.json();
      dispatch(startUserKycSuccessAction(data));
      return data;

    } catch (e: any) {
      // network / CORS, etc. – still swallow
      dispatch(startUserKycFailureAction(e?.message ?? "network error"));
      return null;                                 // ⬅️  don't throw
    }
  };
};